/**
 * Every country a phone field can pick, with its ISO 3166-1 alpha-2
 * code and ITU-T E.164 dial code.
 *
 * Data, not code, and flat on purpose. The picker searches it, a phone
 * field stores only E.164 digits, and detection looks a device ISO up
 * here. Nothing else in the app reads a row.
 *
 * Derived once from Cashbook's MCC-MNC table (first row per country
 * name, which is `getUniqueCountries` there) and then committed. The
 * dump is ~13,500 carrier rows; this is the ~220 unique countries the
 * picker actually shows. ISO 3166 and the numbering plan change about
 * once a year and a country appearing or retiring is a decision, not a
 * build step.
 *
 * What was fixed from the dump, and why:
 *   Abkhazia           ISO `GE` collides with Georgia. Dropped.
 *   International /    not countries. Dropped.
 *   Satellite Networks
 *   Netherlands Antilles  dissolved; Curacao holds 599. Dropped.
 *   South Sudan (Republic of)  duplicate of South Sudan. Dropped.
 *   Puerto Rico, Turks and Caicos, Tuvalu  empty dial codes → 1787, 1649, 688.
 *   Russian Federation  dump said 79 → 7.
 *   American Samoa, British Virgin Islands  missing NANP 1 → 1684, 1284.
 *   Tajikistan, French Guiana, Timor-Leste  wrong ISO → TJ, GF, TL.
 *
 * Names otherwise stay Cashbook's ("Czech Rep.", "Hongkong, China",
 * "Swaziland"). Renaming is a separate copy decision.
 */

export type Country = {
  readonly iso: string;
  readonly name: string;
  /** Digits only, no leading `+`. */
  readonly dialCode: string;
};

export const DEFAULT_COUNTRY_ISO = 'IN';

export const COUNTRIES: readonly Country[] = [
  { iso: 'AF', name: 'Afghanistan', dialCode: '93' },
  { iso: 'AL', name: 'Albania', dialCode: '355' },
  { iso: 'DZ', name: 'Algeria', dialCode: '213' },
  { iso: 'AS', name: 'American Samoa', dialCode: '1684' },
  { iso: 'AD', name: 'Andorra', dialCode: '376' },
  { iso: 'AO', name: 'Angola', dialCode: '244' },
  { iso: 'AI', name: 'Anguilla', dialCode: '1264' },
  { iso: 'AG', name: 'Antigua and Barbuda', dialCode: '1268' },
  { iso: 'AR', name: 'Argentina Republic', dialCode: '54' },
  { iso: 'AM', name: 'Armenia', dialCode: '374' },
  { iso: 'AW', name: 'Aruba', dialCode: '297' },
  { iso: 'AU', name: 'Australia', dialCode: '61' },
  { iso: 'AT', name: 'Austria', dialCode: '43' },
  { iso: 'AZ', name: 'Azerbaijan', dialCode: '994' },
  { iso: 'BS', name: 'Bahamas', dialCode: '1242' },
  { iso: 'BH', name: 'Bahrain', dialCode: '973' },
  { iso: 'BD', name: 'Bangladesh', dialCode: '880' },
  { iso: 'BB', name: 'Barbados', dialCode: '1246' },
  { iso: 'BY', name: 'Belarus', dialCode: '375' },
  { iso: 'BE', name: 'Belgium', dialCode: '32' },
  { iso: 'BZ', name: 'Belize', dialCode: '501' },
  { iso: 'BJ', name: 'Benin', dialCode: '229' },
  { iso: 'BM', name: 'Bermuda', dialCode: '1441' },
  { iso: 'BT', name: 'Bhutan', dialCode: '975' },
  { iso: 'BO', name: 'Bolivia', dialCode: '591' },
  { iso: 'BA', name: 'Bosnia & Herzegov.', dialCode: '387' },
  { iso: 'BW', name: 'Botswana', dialCode: '267' },
  { iso: 'BR', name: 'Brazil', dialCode: '55' },
  { iso: 'VG', name: 'British Virgin Islands', dialCode: '1284' },
  { iso: 'BN', name: 'Brunei Darussalam', dialCode: '673' },
  { iso: 'BG', name: 'Bulgaria', dialCode: '359' },
  { iso: 'BF', name: 'Burkina Faso', dialCode: '226' },
  { iso: 'BI', name: 'Burundi', dialCode: '257' },
  { iso: 'KH', name: 'Cambodia', dialCode: '855' },
  { iso: 'CM', name: 'Cameroon', dialCode: '237' },
  { iso: 'CA', name: 'Canada', dialCode: '1' },
  { iso: 'CV', name: 'Cape Verde', dialCode: '238' },
  { iso: 'KY', name: 'Cayman Islands', dialCode: '1345' },
  { iso: 'CF', name: 'Central African Rep.', dialCode: '236' },
  { iso: 'TD', name: 'Chad', dialCode: '235' },
  { iso: 'CL', name: 'Chile', dialCode: '56' },
  { iso: 'CN', name: 'China', dialCode: '86' },
  { iso: 'CO', name: 'Colombia', dialCode: '57' },
  { iso: 'KM', name: 'Comoros', dialCode: '269' },
  { iso: 'CD', name: 'Congo, Dem. Rep.', dialCode: '243' },
  { iso: 'CG', name: 'Congo, Republic', dialCode: '242' },
  { iso: 'CK', name: 'Cook Islands', dialCode: '682' },
  { iso: 'CR', name: 'Costa Rica', dialCode: '506' },
  { iso: 'HR', name: 'Croatia', dialCode: '385' },
  { iso: 'CU', name: 'Cuba', dialCode: '53' },
  { iso: 'CW', name: 'Curacao', dialCode: '599' },
  { iso: 'CY', name: 'Cyprus', dialCode: '357' },
  { iso: 'CZ', name: 'Czech Rep.', dialCode: '420' },
  { iso: 'DK', name: 'Denmark', dialCode: '45' },
  { iso: 'DJ', name: 'Djibouti', dialCode: '253' },
  { iso: 'DM', name: 'Dominica', dialCode: '1767' },
  { iso: 'DO', name: 'Dominican Republic', dialCode: '1809' },
  { iso: 'EC', name: 'Ecuador', dialCode: '593' },
  { iso: 'EG', name: 'Egypt', dialCode: '20' },
  { iso: 'SV', name: 'El Salvador', dialCode: '503' },
  { iso: 'GQ', name: 'Equatorial Guinea', dialCode: '240' },
  { iso: 'ER', name: 'Eritrea', dialCode: '291' },
  { iso: 'EE', name: 'Estonia', dialCode: '372' },
  { iso: 'ET', name: 'Ethiopia', dialCode: '251' },
  { iso: 'FK', name: 'Falkland Islands (Malvinas)', dialCode: '500' },
  { iso: 'FO', name: 'Faroe Islands', dialCode: '298' },
  { iso: 'FJ', name: 'Fiji', dialCode: '679' },
  { iso: 'FI', name: 'Finland', dialCode: '358' },
  { iso: 'FR', name: 'France', dialCode: '33' },
  { iso: 'GF', name: 'French Guiana', dialCode: '594' },
  { iso: 'PF', name: 'French Polynesia', dialCode: '689' },
  { iso: 'GA', name: 'Gabon', dialCode: '241' },
  { iso: 'GM', name: 'Gambia', dialCode: '220' },
  { iso: 'GE', name: 'Georgia', dialCode: '995' },
  { iso: 'DE', name: 'Germany', dialCode: '49' },
  { iso: 'GH', name: 'Ghana', dialCode: '233' },
  { iso: 'GI', name: 'Gibraltar', dialCode: '350' },
  { iso: 'GR', name: 'Greece', dialCode: '30' },
  { iso: 'GL', name: 'Greenland', dialCode: '299' },
  { iso: 'GD', name: 'Grenada', dialCode: '1473' },
  { iso: 'GP', name: 'Guadeloupe', dialCode: '590' },
  { iso: 'GU', name: 'Guam', dialCode: '1671' },
  { iso: 'GT', name: 'Guatemala', dialCode: '502' },
  { iso: 'GN', name: 'Guinea', dialCode: '224' },
  { iso: 'GW', name: 'Guinea-Bissau', dialCode: '245' },
  { iso: 'GY', name: 'Guyana', dialCode: '592' },
  { iso: 'HT', name: 'Haiti', dialCode: '509' },
  { iso: 'HN', name: 'Honduras', dialCode: '504' },
  { iso: 'HK', name: 'Hongkong, China', dialCode: '852' },
  { iso: 'HU', name: 'Hungary', dialCode: '36' },
  { iso: 'IS', name: 'Iceland', dialCode: '354' },
  { iso: 'IN', name: 'India', dialCode: '91' },
  { iso: 'ID', name: 'Indonesia', dialCode: '62' },
  { iso: 'IR', name: 'Iran', dialCode: '98' },
  { iso: 'IQ', name: 'Iraq', dialCode: '964' },
  { iso: 'IE', name: 'Ireland', dialCode: '353' },
  { iso: 'IL', name: 'Israel', dialCode: '972' },
  { iso: 'IT', name: 'Italy', dialCode: '39' },
  { iso: 'CI', name: 'Ivory Coast', dialCode: '225' },
  { iso: 'JM', name: 'Jamaica', dialCode: '1876' },
  { iso: 'JP', name: 'Japan', dialCode: '81' },
  { iso: 'JO', name: 'Jordan', dialCode: '962' },
  { iso: 'KZ', name: 'Kazakhstan', dialCode: '7' },
  { iso: 'KE', name: 'Kenya', dialCode: '254' },
  { iso: 'KI', name: 'Kiribati', dialCode: '686' },
  { iso: 'KP', name: 'Korea N., Dem. People\'s Rep.', dialCode: '850' },
  { iso: 'KR', name: 'Korea S, Republic of', dialCode: '82' },
  { iso: 'KW', name: 'Kuwait', dialCode: '965' },
  { iso: 'KG', name: 'Kyrgyzstan', dialCode: '996' },
  { iso: 'LA', name: 'Laos P.D.R.', dialCode: '856' },
  { iso: 'LV', name: 'Latvia', dialCode: '371' },
  { iso: 'LB', name: 'Lebanon', dialCode: '961' },
  { iso: 'LS', name: 'Lesotho', dialCode: '266' },
  { iso: 'LR', name: 'Liberia', dialCode: '231' },
  { iso: 'LY', name: 'Libya', dialCode: '218' },
  { iso: 'LI', name: 'Liechtenstein', dialCode: '423' },
  { iso: 'LT', name: 'Lithuania', dialCode: '370' },
  { iso: 'LU', name: 'Luxembourg', dialCode: '352' },
  { iso: 'MO', name: 'Macao, China', dialCode: '853' },
  { iso: 'MK', name: 'Macedonia', dialCode: '389' },
  { iso: 'MG', name: 'Madagascar', dialCode: '261' },
  { iso: 'MW', name: 'Malawi', dialCode: '265' },
  { iso: 'MY', name: 'Malaysia', dialCode: '60' },
  { iso: 'MV', name: 'Maldives', dialCode: '960' },
  { iso: 'ML', name: 'Mali', dialCode: '223' },
  { iso: 'MT', name: 'Malta', dialCode: '356' },
  { iso: 'MQ', name: 'Martinique (French Department of)', dialCode: '596' },
  { iso: 'MR', name: 'Mauritania', dialCode: '222' },
  { iso: 'MU', name: 'Mauritius', dialCode: '230' },
  { iso: 'MX', name: 'Mexico', dialCode: '52' },
  { iso: 'FM', name: 'Micronesia', dialCode: '691' },
  { iso: 'MD', name: 'Moldova', dialCode: '373' },
  { iso: 'MC', name: 'Monaco', dialCode: '377' },
  { iso: 'MN', name: 'Mongolia', dialCode: '976' },
  { iso: 'ME', name: 'Montenegro', dialCode: '382' },
  { iso: 'MS', name: 'Montserrat', dialCode: '1664' },
  { iso: 'MA', name: 'Morocco', dialCode: '212' },
  { iso: 'MZ', name: 'Mozambique', dialCode: '258' },
  { iso: 'MM', name: 'Myanmar (Burma)', dialCode: '95' },
  { iso: 'NA', name: 'Namibia', dialCode: '264' },
  { iso: 'NP', name: 'Nepal', dialCode: '977' },
  { iso: 'NL', name: 'Netherlands', dialCode: '31' },
  { iso: 'NC', name: 'New Caledonia', dialCode: '687' },
  { iso: 'NZ', name: 'New Zealand', dialCode: '64' },
  { iso: 'NI', name: 'Nicaragua', dialCode: '505' },
  { iso: 'NE', name: 'Niger', dialCode: '227' },
  { iso: 'NG', name: 'Nigeria', dialCode: '234' },
  { iso: 'NU', name: 'Niue', dialCode: '683' },
  { iso: 'NO', name: 'Norway', dialCode: '47' },
  { iso: 'OM', name: 'Oman', dialCode: '968' },
  { iso: 'PK', name: 'Pakistan', dialCode: '92' },
  { iso: 'PW', name: 'Palau (Republic of)', dialCode: '680' },
  { iso: 'PS', name: 'Palestinian Territory', dialCode: '970' },
  { iso: 'PA', name: 'Panama', dialCode: '507' },
  { iso: 'PG', name: 'Papua New Guinea', dialCode: '675' },
  { iso: 'PY', name: 'Paraguay', dialCode: '595' },
  { iso: 'PE', name: 'Peru', dialCode: '51' },
  { iso: 'PH', name: 'Philippines', dialCode: '63' },
  { iso: 'PL', name: 'Poland', dialCode: '48' },
  { iso: 'PT', name: 'Portugal', dialCode: '351' },
  { iso: 'PR', name: 'Puerto Rico', dialCode: '1787' },
  { iso: 'QA', name: 'Qatar', dialCode: '974' },
  { iso: 'RE', name: 'Reunion', dialCode: '262' },
  { iso: 'RO', name: 'Romania', dialCode: '40' },
  { iso: 'RU', name: 'Russian Federation', dialCode: '7' },
  { iso: 'RW', name: 'Rwanda', dialCode: '250' },
  { iso: 'KN', name: 'Saint Kitts and Nevis', dialCode: '1869' },
  { iso: 'LC', name: 'Saint Lucia', dialCode: '1758' },
  { iso: 'WS', name: 'Samoa', dialCode: '685' },
  { iso: 'SM', name: 'San Marino', dialCode: '378' },
  { iso: 'ST', name: 'Sao Tome & Principe', dialCode: '239' },
  { iso: 'SA', name: 'Saudi Arabia', dialCode: '966' },
  { iso: 'SN', name: 'Senegal', dialCode: '221' },
  { iso: 'RS', name: 'Serbia', dialCode: '381' },
  { iso: 'SC', name: 'Seychelles', dialCode: '248' },
  { iso: 'SL', name: 'Sierra Leone', dialCode: '232' },
  { iso: 'SG', name: 'Singapore', dialCode: '65' },
  { iso: 'SK', name: 'Slovakia', dialCode: '421' },
  { iso: 'SI', name: 'Slovenia', dialCode: '386' },
  { iso: 'SB', name: 'Solomon Islands', dialCode: '677' },
  { iso: 'SO', name: 'Somalia', dialCode: '252' },
  { iso: 'ZA', name: 'South Africa', dialCode: '27' },
  { iso: 'SS', name: 'South Sudan', dialCode: '211' },
  { iso: 'ES', name: 'Spain', dialCode: '34' },
  { iso: 'LK', name: 'Sri Lanka', dialCode: '94' },
  { iso: 'PM', name: 'St. Pierre & Miquelon', dialCode: '508' },
  { iso: 'VC', name: 'St. Vincent & Gren.', dialCode: '1784' },
  { iso: 'SD', name: 'Sudan', dialCode: '249' },
  { iso: 'SR', name: 'Suriname', dialCode: '597' },
  { iso: 'SZ', name: 'Swaziland', dialCode: '268' },
  { iso: 'SE', name: 'Sweden', dialCode: '46' },
  { iso: 'CH', name: 'Switzerland', dialCode: '41' },
  { iso: 'SY', name: 'Syrian Arab Republic', dialCode: '963' },
  { iso: 'TW', name: 'Taiwan', dialCode: '886' },
  { iso: 'TJ', name: 'Tajikistan', dialCode: '992' },
  { iso: 'TZ', name: 'Tanzania', dialCode: '255' },
  { iso: 'TH', name: 'Thailand', dialCode: '66' },
  { iso: 'TL', name: 'Timor-Leste', dialCode: '670' },
  { iso: 'TG', name: 'Togo', dialCode: '228' },
  { iso: 'TO', name: 'Tonga', dialCode: '676' },
  { iso: 'TT', name: 'Trinidad and Tobago', dialCode: '1868' },
  { iso: 'TN', name: 'Tunisia', dialCode: '216' },
  { iso: 'TR', name: 'Turkey', dialCode: '90' },
  { iso: 'TM', name: 'Turkmenistan', dialCode: '993' },
  { iso: 'TC', name: 'Turks and Caicos Islands', dialCode: '1649' },
  { iso: 'TV', name: 'Tuvalu', dialCode: '688' },
  { iso: 'UG', name: 'Uganda', dialCode: '256' },
  { iso: 'UA', name: 'Ukraine', dialCode: '380' },
  { iso: 'AE', name: 'United Arab Emirates', dialCode: '971' },
  { iso: 'GB', name: 'United Kingdom', dialCode: '44' },
  { iso: 'US', name: 'United States', dialCode: '1' },
  { iso: 'UY', name: 'Uruguay', dialCode: '598' },
  { iso: 'UZ', name: 'Uzbekistan', dialCode: '998' },
  { iso: 'VU', name: 'Vanuatu', dialCode: '678' },
  { iso: 'VE', name: 'Venezuela', dialCode: '58' },
  { iso: 'VN', name: 'Viet Nam', dialCode: '84' },
  { iso: 'VI', name: 'Virgin Islands, U.S.', dialCode: '1340' },
  { iso: 'YE', name: 'Yemen', dialCode: '967' },
  { iso: 'ZM', name: 'Zambia', dialCode: '260' },
  { iso: 'ZW', name: 'Zimbabwe', dialCode: '263' },
];

/**
 * When several countries share a dial code, parse and the first-open chip
 * land on this one. Cashbook's `findByCountryCode` returns the first row in
 * dump order, which for `1` is Canada; a shop's contacts are overwhelmingly
 * US numbers written `+1…`. The other shared codes get the sovereign, not
 * the leftover of a dissolved territory.
 */
const PREFERRED_FOR_DIAL_CODE: Readonly<Record<string, string>> = {
  '1': 'US',
  '7': 'RU',
  '599': 'CW',
  '211': 'SS',
};

const BY_ISO: ReadonlyMap<string, Country> = new Map(
  COUNTRIES.map((country) => [country.iso, country]),
);

const BY_DIAL: ReadonlyMap<string, Country> = (() => {
  const map = new Map<string, Country>();
  for (const country of COUNTRIES) {
    if (!map.has(country.dialCode)) map.set(country.dialCode, country);
  }
  for (const country of COUNTRIES) {
    if (PREFERRED_FOR_DIAL_CODE[country.dialCode] === country.iso) {
      map.set(country.dialCode, country);
    }
  }
  return map;
})();

const DIAL_CODES: ReadonlySet<string> = new Set(COUNTRIES.map((country) => country.dialCode));

export const countryFor = (iso: string): Country | undefined =>
  BY_ISO.get(iso.trim().toUpperCase());

export const countryForDialCode = (dialCode: string): Country | undefined => {
  const digits = dialCode.trim().replace(/^\+/, '');
  return BY_DIAL.get(digits);
};

export const dialCodeForIso = (iso: string): string | null => countryFor(iso)?.dialCode ?? null;

/**
 * The country a BCP 47 locale names, when it is one a phone field can pick:
 * `en-IN` is IN, `zh-Hans-CN` is CN, `en_US` is US. Null for a locale with no
 * region subtag (`en`), a numeric region (`es-419`), or a region the table
 * does not ship. A business's locale (`app.tenant.locale`) is read this way
 * for the country its phone fields open on.
 */
export const countryOfLocale = (locale: string | null | undefined): string | null => {
  if (typeof locale !== 'string') return null;
  for (const part of locale.trim().split(/[-_]/).slice(1)) {
    if (!/^[A-Za-z]{2}$/.test(part)) continue;
    return countryFor(part)?.iso ?? null;
  }
  return null;
};

/**
 * Split a stored E.164 value into the country the chip should show and the
 * national digits the box should hold.
 *
 * Longest prefix wins, so `+1268…` is Antigua rather than the US. Shared
 * codes (`+1`, `+7`) map through `PREFERRED_FOR_DIAL_CODE`. Anything that is
 * not `+` and digits, or whose prefix is not a code we ship, is null.
 */
export const splitE164 = (stored: string): { iso: string; national: string } | null => {
  const trimmed = stored.trim();
  if (!trimmed.startsWith('+')) return null;
  const digits = trimmed.slice(1);
  if (!/^\d+$/.test(digits)) return null;
  for (let length = Math.min(4, digits.length); length >= 1; length -= 1) {
    const prefix = digits.slice(0, length);
    if (!DIAL_CODES.has(prefix)) continue;
    const country = countryForDialCode(prefix);
    if (country === undefined) continue;
    return { iso: country.iso, national: digits.slice(length) };
  }
  return null;
};

/**
 * The rows a query matches, ranked the way Cashbook's country list ranks them.
 *
 * Empty → the full list in name order. A numeric query (optional leading `+`)
 * matches dial-code prefixes: exact first, then shorter, then name. A name
 * query matches `includes`, with `startsWith` above the rest.
 */
export const searchCountries = (query: string): readonly Country[] => {
  const raw = query.trim();
  if (raw === '') return COUNTRIES;

  const numericQuery = raw.replace(/^\+/, '').replace(/\s+/g, '');
  const isCodeQuery = numericQuery.length > 0 && /^\d+$/.test(numericQuery);

  if (isCodeQuery) {
    return COUNTRIES.filter((country) => country.dialCode.startsWith(numericQuery)).sort((a, b) => {
      const aExact = a.dialCode === numericQuery;
      const bExact = b.dialCode === numericQuery;
      if (aExact !== bExact) return aExact ? -1 : 1;
      if (a.dialCode.length !== b.dialCode.length) return a.dialCode.length - b.dialCode.length;
      return a.name.localeCompare(b.name);
    });
  }

  const lower = raw.toLowerCase();
  return COUNTRIES.filter((country) => country.name.toLowerCase().includes(lower)).sort((a, b) => {
    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();
    const aStarts = aName.startsWith(lower);
    const bStarts = bName.startsWith(lower);
    if (aStarts !== bStarts) return aStarts ? -1 : 1;
    return aName.localeCompare(bName);
  });
};

